import * as Bixby from '$/core';
import AnalyticsService from './AnalyticsService';
import { PlannerTranscriptionService } from './PlannerTranscriptionService';

export abstract class NewsAdminService {
  private static baseUrl = `${Bixby.Constants.API_ADMIN_PREFIX}/${Bixby.Constants.API_NEWSCONTROLLER}`;

  public static async createNews(payload: Bixby.Models.CreateNewsRequest): Promise<void> {
    // Create planner transcription task if necessary
    if (payload.hasTranscriptionTask) {
      const plannerTranscriptionCreateId: string = await PlannerTranscriptionService.addTranscriptionCreateTaskAsync(
        payload.transcriptionDetails
      );
      payload.transcriptionDetails.plannerTranscriptionCreateId = plannerTranscriptionCreateId;
    }

    try {
      const route = Bixby.API.ServerRoute.forAction(NewsAdminService.baseUrl);
      await Bixby.API.post(route, payload);
    } catch (e: any) {
      // Error creating news. Delete planner transcription task
      await PlannerTranscriptionService.deleteTranscriptionTaskAsync(
        payload.transcriptionDetails.plannerTranscriptionCreateId
      );
      throw e;
    }
  }

  public static async getNews(): Promise<Bixby.Models.AdminNews[]> {
    const url = `${NewsAdminService.baseUrl}/${Bixby.Constants.API_ACTION_SEARCH}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    return await Bixby.API.get(route);
  }

  public static async getNewsByCompanyId(companyId: string): Promise<Bixby.Models.AdminNews[]> {
    const url = `${NewsAdminService.baseUrl}/${Bixby.Constants.API_ACTION_SEARCH}`;
    const qs = Bixby.Utils.objectToQueryString({ companyId });
    const route = Bixby.API.ServerRoute.forAction(`${url}${qs}`);
    return await Bixby.API.get(route);
  }
  
  public static async getNewsByCompanyIdAndNewsTypeId(companyId: string, newsTypeIds: string): Promise<Bixby.Models.AdminNews[]> {
    const url = `${NewsAdminService.baseUrl}/${Bixby.Constants.API_ACTION_SEARCH}`;
    const qs = Bixby.Utils.objectToQueryString({ companyId, newsTypeIds });
    const route = Bixby.API.ServerRoute.forAction(`${url}${qs}`);
    return await Bixby.API.get(route);
  }

  public static async getById(id: string): Promise<Bixby.Models.AdminNews> {
    const route = Bixby.API.ServerRoute.forAction(`${NewsAdminService.baseUrl}/${id}`);
    return await Bixby.API.get(route);
  }

  public static async removeNews(news: Bixby.Models.AdminNews, retainTranscription: boolean): Promise<void> {
    if (!retainTranscription) {
      // Delete transcription pending task if present
      if (!Bixby.Utils.isNullOrEmpty(news.plannerTranscriptionCreateId)) {
        try {
          if (news.plannerTranscriptionCreateId) {
            await PlannerTranscriptionService.deleteTranscriptionTaskAsync(news.plannerTranscriptionCreateId);
          }
        } catch (e: any) {
          AnalyticsService.exception(e);
        }
      }

      // Delete transcription approval task if present
      if (!Bixby.Utils.isNullOrEmpty(news.plannerTranscriptionApproveId)) {
        try {
          if (news.plannerTranscriptionApproveId) {
            await PlannerTranscriptionService.deleteTranscriptionTaskAsync(news.plannerTranscriptionApproveId);
          }
        } catch (e: any) {
          AnalyticsService.exception(e);
        }
      }
    }

    const qs = Bixby.Utils.objectToQueryString({ id: news.id, retainTranscription });
    const route = Bixby.API.ServerRoute.forAction(`${NewsAdminService.baseUrl}${qs}`);
    return await Bixby.API.delete(route);
  }

  public static async publishNews(news: Bixby.Models.AdminNews): Promise<void> {
    //allow any server exceptions through so that client error handling logic is processed
    const route = Bixby.API.ServerRoute.forAction(`${NewsAdminService.baseUrl}/${news.id}`);
    return await Bixby.API.post(route, news.id);
  }
}
